/* eslint-disable no-unused-vars */
/* global PLATFORM, G */
import map from 'lib/sequence/model/api/map'
import find from 'lib/sequence/component/children/find'
import { get, set } from 'lib/sequence/component/state/value'
import { asyncPipeSpread, asyncPipeSpreadIf, bulk, getFirstItem, isArr, isNum, isObj, setKey } from 'lib/util'
import session, { settings } from 'app/_shared/session'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { list } from 'app/_shared/events'
import displayFavoriteButtonsFromModel from 'app/_shared/action/partial/favorite/display'

const bulkHide = bulk(hide)

/**
 * Returns the value of the `status` model attribute, regardless of its many possible shapes.
 *
 * @param {Object} status
 * @returns {*|x is number|x is Array|x is Object}
 */
const getStatusValue = status => (status[G.CACHE] && isNum(status[G.CACHE]) && status[G.CACHE])
  || (status[G.CACHE] && isArr(status[G.CACHE]) && status[G.CACHE]?.[0]?.key)
  || (status[G.CACHE] && isObj(status[G.CACHE]) && status[G.CACHE]?.key)

/**
 * Maps needed properties to components.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapProperties = module => async (components, ...args) => {
  const model = module[G.MODEL]

  const bulkMapModel = bulk(map(model))

  const { info, mobileInfo } = components
  const targetComponent = PLATFORM === 'web' ? info : mobileInfo

  // TODO: Check if web still works

  const {
    statusBox,
    typeBox,
    requesterBox,
    assigneeBox,
    infoBox,
    descriptionBox,
    itemInfo,
  } = find(targetComponent)

  const mappableComponents = [
    statusBox,
    typeBox,
    requesterBox,
    itemInfo,
    infoBox,
    assigneeBox,
    descriptionBox,
  ].filter(x => !!x)

  bulkMapModel(...mappableComponents)

  return [components, ...args]
}

/**
 * May swap the `serial` and `name` order depending on tenant settings.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const swapSerialAndName = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { itemData } = model[G.CHILDREN]

  const { itemInfo } = components
  const { serial: serialLabel, name: nameLabel } = find(itemInfo)

  const { serialDistinction, serial, name } = itemData[G.CACHE] || {}
  const serialValue = serialDistinction || serial

  settings.swapDeviceSerialAndName
    ? set(nameLabel, serialValue)
    && set(serialLabel, name)
    : set(nameLabel, name)
    && set(serialLabel, serialValue)

  return [components, ...args]
}

/**
 * Presets the message.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetMessage = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { message } = components

  const sourceId = model[G.STATE][G.REF]
  const userId = session(module).user.key()
  !get(message) && set(message, { sourceId, userId })

  return [components, ...args]
}

/**
 * Presets the message field.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetMessageField = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { messageField } = components

  const { status, type } = model[G.CHILDREN]
  const currentStatus = getStatusValue(status)

  setKey(currentStatus, 'currentStatus', messageField[G.STATE])
  setKey(type[G.CACHE], 'currentType', messageField[G.STATE])

  // Setting the message text in case there is one in the module state.
  module[G.STATE].text && set(messageField, { text: module[G.STATE].text })

  return [components, ...args]
}

/**
 * Displays the message field and reopen box depending on the request status.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayMessageField = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { status, reopenBlocked } = model[G.CHILDREN]
  const currentStatus = getStatusValue(status)
  const blocked = reopenBlocked[G.CACHE]

  const actions = getFirstItem(module[G.STATE][G.ACTION][G.COMPONENT][G.ACTIONS])

  // PLATFORM === 'web' && hide(actions)

  const { reopenBox, messageField } = components

  const requestStatus = parseInt(currentStatus, 10)
  if (requestStatus >= 10 && requestStatus < 80) {
    show(messageField)
    PLATFORM === 'web' && hide(reopenBox)
    PLATFORM === 'mobile' && hide(actions)
  } else {
    hide(messageField)
    PLATFORM === 'web' && (blocked ? hide(reopenBox) : show(reopenBox))
    PLATFORM === 'mobile' && (blocked ? hide(actions) : show(actions))
  }

  return [components, ...args]
}

/**
 * Displays the service item
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayItem = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { item } = model[G.CHILDREN]

  const { itemBox, itemInfo } = components
  const { serial, name } = find(itemInfo)

  const serialValue = get(serial)
  const nameValue = get(name)

  !item[G.STATE]?.[G.REF]
    && !serialValue
    && !nameValue
    && hide(itemBox)

  return args
}

/**
 * Presets and displays the type
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetType = module => async (components, ...args) => {
  const { [G.INTL]: intl } = module[G.ADAPTER]

  const model = module[G.MODEL]
  const modelCache = model[G.CACHE]
  const { value: { submit: { timestamp: submitTimestamp } = {} } = {} } = modelCache

  const { typeBox } = components
  const { type, submit } = find(typeBox)

  const [{ value } = {}] = get(type) || []

  set(type, value)
  set(submit, ` • ${intl.getLocaleDate(submitTimestamp)}`)

  return [components, ...args]
}

/**
 * Presets the attachments for the request
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetAttachments = module => async (components, ...args) => {
  const { fileAttachments, imageAttachments } = components
  const { attachment: fileGallery } = find(fileAttachments)
  const { attachment: imageGallery } = find(imageAttachments)

  const data = await list(null, module, fileGallery, null)

  if (data?.length) {
    const hasImages = data.some(f => f?.value?.mimetype?.includes('image'))
    const hasFiles = data.some(f => !f?.value?.mimetype?.includes('image'))

    !hasImages
      ? hide(imageAttachments)
      : setKey(data, 'data', imageGallery[G.STATE])
    !hasFiles
      ? hide(fileAttachments)
      : setKey(data, 'data', fileGallery[G.STATE])
  } else {
    hide(fileAttachments)
    hide(imageAttachments)
  }

  return [components, ...args]
}

/**
 * Presets and displays the requester
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetAndDisplayRequester = module => async (components, ...args) => {
  const { requesterBox, requesterDivider } = components
  const { submitter: submitterField, requesterContactOrg: requesterOrgField } = find(requesterBox)

  const submitterState = get(submitterField)
  const { refs: { person: personRef = {} } = {} } = getFirstItem(submitterState || []) || {}
  const { value: { firstName, lastName, name = null } = {} } = getFirstItem(personRef || []) || {}
  const { value: { name: org = '-' } = {} } = getFirstItem(get(requesterOrgField) || []) || {}

  const fullName = name?.length ? name : `${firstName} ${lastName}`

  if (!submitterState) {
    bulkHide(requesterBox, requesterDivider)
  } else {
    set(submitterField, fullName)
    set(requesterOrgField, org)
  }

  return [components, ...args]
}

/**
 * Presets and displays the service item
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const presetAndDisplayItem = module => async (components, ...args) => {
  const { itemInfo, itemDivider } = components
  const { item: itemField, serial: serialField } = find(itemInfo)

  const itemState = get(itemField)
  const { value: { name = '-', serial = '-' } = {} } = getFirstItem(itemState || []) || {}

  if (!itemState) {
    bulkHide(itemInfo, itemDivider)
  } else {
    set(itemField, name)
    set(serialField, serial)
  }

  return [components, ...args]
}

/**
 * Displays the description
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const PresetAndDisplayDescription = module => async (components, ...args) => {
  const { descriptionBox } = components
  const { description: descriptionField } = find(descriptionBox)
  const { value: { description: { text = null } = {} } = {} } = module[G.MODEL][G.CACHE] || {}

  text
    ? set(descriptionField, text)
    : hide(descriptionBox)

  return [components, ...args]
}

/**
 * Request Detail Action
 *
 * Check {@link listWithRefsAndSetPartner} too see logic for setting assignee
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  displayFavoriteButtonsFromModel(module),
  mapProperties(module),
  presetMessage(module),
  presetMessageField(module),
  displayMessageField(module),
  asyncPipeSpreadIf(() => PLATFORM === 'web')(
    swapSerialAndName(module),
    displayItem(module),
  ),
  asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
    presetType(module),
    presetAttachments(module),
    presetAndDisplayRequester(module),
    presetAndDisplayItem(module),
    PresetAndDisplayDescription(module),
  ),
)(find(component), ...args)
